import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Smartphone, Trash2, Radio, Inbox } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState({ token: "", platform: "fcm_android", device_label: "" });

  const load = async () => {
    const [p, q] = await Promise.all([
      api.get("/v1/user/profile").then(r => r.data.data),
      api.get("/v1/push-queue/pending").then(r => r.data),
    ]);
    setProfile(p);
    setPending(q.items || []);
  };
  useEffect(() => { load(); }, []);

  // Existing tokens live on the user document; expose them through /auth/me + a tiny read
  const myTokens = (user && user.device_tokens) || [];

  const register = async () => {
    if (!form.token.trim()) return toast.error("Paste a token first");
    try {
      await api.post("/v1/device-tokens", form);
      toast.success("Device registered");
      setForm({ ...form, token: "", device_label: "" });
      await refresh(); // refresh /auth/me so device_tokens propagates
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    await api.delete(`/v1/device-tokens/${encodeURIComponent(t)}`);
    await refresh();
    load();
    toast.success("Removed");
  };

  const ackOne = async (id) => {
    await api.post(`/v1/push-queue/${id}/ack`);
    load();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="settings-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Native Handover</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Settings · Devices &amp; Push</h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Register a fake APNs/FCM device token to test the silent-push fan-out
        live. Publishing a call sheet will queue one payload per registered device.
      </p>

      {/* RESOLVED PROFILE PREVIEW */}
      {profile && (
        <div className="mt-8 cs-card rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">/api/v1/user/profile</div>
          <div className="grid sm:grid-cols-3 gap-6 mt-4">
            <Resolved label="Skin Class" value={profile.theme_token.skin_class}
              color={profile.theme_token.hex_color} />
            <Resolved label="Density Mode" value={profile.theme_token.density_mode} />
            <Resolved label="Card ID" value={profile.digital_icard.card_id} mono />
            <Resolved label="Primary Association"
              value={`${profile.primary_association.short_name} · ${profile.primary_association.state}`} />
            <Resolved label="Allowed Roles"
              value={(profile.primary_association.allowed_roles || []).join(", ") || "—"} />
            <Resolved label="Override Active"
              value={profile.project_override.is_active ? "YES" : "no"} />
          </div>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Device token register */}
        <div className="cs-card rounded-2xl p-6" data-testid="device-tokens-panel">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone size={18} style={{ color: "var(--association-theme)" }} />
            <div className="font-display text-xl uppercase tracking-tight">Device Tokens</div>
          </div>

          <div className="space-y-3">
            <Field label="Platform">
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger className="bg-black/40 border-white/15" data-testid="dev-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fcm_android">FCM · Android</SelectItem>
                  <SelectItem value="apns_ios">APNs · iOS</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Token (paste from native client)">
              <Input value={form.token} data-testid="dev-token" placeholder="device_apns_or_fcm_token_string"
                onChange={(e) => setForm({ ...form, token: e.target.value })}
                className="bg-black/40 border-white/15 font-mono text-xs" />
            </Field>
            <Field label="Device Label (optional)">
              <Input value={form.device_label} data-testid="dev-label" placeholder="iPhone 15 Pro / Pixel 8"
                onChange={(e) => setForm({ ...form, device_label: e.target.value })}
                className="bg-black/40 border-white/15" />
            </Field>
            <Button onClick={register} className="btn-themed w-full h-11 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="dev-register">
              Register Device
            </Button>
          </div>

          <div className="mt-7 pt-5 border-t border-white/5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Registered devices</div>
            <div className="mt-3 space-y-2" data-testid="dev-list">
              {myTokens.length === 0 && <div className="text-xs text-white/40">No devices registered.</div>}
              {myTokens.map((d) => (
                <div key={d.token} className="flex items-center gap-3 border border-white/5 rounded-lg p-3">
                  <Radio size={14} style={{ color: "var(--association-theme)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono truncate">{d.token}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5">
                      {d.platform} {d.device_label ? `· ${d.device_label}` : ""}
                    </div>
                  </div>
                  <button onClick={() => remove(d.token)} className="text-white/40 hover:text-red-400" data-testid={`dev-rm-${d.token}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pending push queue */}
        <div className="cs-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Inbox size={18} style={{ color: "var(--association-theme)" }} />
            <div className="font-display text-xl uppercase tracking-tight">Push Queue · Pending</div>
          </div>
          <div className="text-xs text-white/50 mb-4">
            Each entry below is the ready-to-ship FCM+APNs payload.
            Publish a call sheet from <span className="text-white">Attendance</span> to trigger fan-out.
          </div>

          <div className="space-y-3 max-h-[500px] overflow-auto" data-testid="push-queue-list">
            {pending.length === 0 && <div className="text-xs text-white/40">Queue empty.</div>}
            {pending.map((p) => (
              <div key={p.id} className="border border-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#F5B82E]">
                    {p.platform || "no-device"} · {p.priority}
                  </div>
                  <button onClick={() => ackOne(p.id)} className="text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-white"
                          data-testid={`push-ack-${p.id}`}>
                    Mark delivered ✓
                  </button>
                </div>
                <pre className="text-[10px] font-mono text-white/65 bg-black/40 p-2 rounded overflow-auto">
{JSON.stringify(p.payload, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Resolved({ label, value, color, mono }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/45">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : "font-display"} text-base flex items-center gap-2`}>
        {color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}
